import { useMemo } from 'react';
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip, LayerGroup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { SalesOutlet, CompanyRow } from './types';
import { scoreColor, CATEGORY_LABEL, formatDistance } from './lib';

interface Props {
  outlets: SalesOutlet[];
  rows: CompanyRow[];
  selectedOutletId?: string;
  onSelectCompany?: (id: string) => void;
}

// Sensible city centers as a fallback if no outlet is selected.
const CITY_CENTER: Record<string, [number, number]> = {
  Mumbai: [19.076, 72.8777],
  Bengaluru: [12.9716, 77.5946],
  Goa: [15.4909, 73.8278],
};

export function ResearchMap({ outlets, rows, selectedOutletId, onSelectCompany }: Props) {
  const withGeo = useMemo(() => rows.filter((r) => r.lat != null && r.lng != null), [rows]);

  const center = useMemo<[number, number]>(() => {
    const o = outlets.find((x) => x.outlet_id === selectedOutletId) ?? outlets[0];
    if (o?.lat != null && o?.lng != null) return [o.lat, o.lng];
    const cityCenter = o?.city ? CITY_CENTER[o.city] : undefined;
    if (cityCenter) return cityCenter;
    return [19.076, 72.8777];
  }, [outlets, selectedOutletId]);

  const zoom = selectedOutletId ? 14 : 11;

  return (
    <MapContainer
      key={selectedOutletId ?? 'all'}
      center={center}
      zoom={zoom}
      scrollWheelZoom
      style={{ height: '100%', width: '100%', borderRadius: 12, zIndex: 0 }}
    >
      <TileLayer
        attribution='&copy; OpenStreetMap contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* Outlet pins + research radius rings */}
      <LayerGroup>
        {outlets
          .filter((o) => o.lat != null && o.lng != null)
          .filter((o) => !selectedOutletId || o.outlet_id === selectedOutletId)
          .map((o) => (
            <LayerGroup key={o.outlet_id}>
              <Circle
                center={[o.lat!, o.lng!]}
                radius={(o.research_radius_km ?? 3) * 1000}
                pathOptions={{ color: '#252D63', weight: 1, fillColor: '#252D63', fillOpacity: 0.05 }}
              />
              <CircleMarker
                center={[o.lat!, o.lng!]}
                radius={8}
                pathOptions={{ color: '#fff', weight: 2, fillColor: '#252D63', fillOpacity: 1 }}
              >
                <Tooltip direction="top" offset={[0, -6]}>
                  <strong>{o.name}</strong>
                  <div>{o.city} · {o.research_radius_km} km radius</div>
                </Tooltip>
              </CircleMarker>
            </LayerGroup>
          ))}
      </LayerGroup>

      {/* Company markers coloured by score */}
      <LayerGroup>
        {withGeo.map((r) => (
          <CircleMarker
            key={r.id}
            center={[r.lat!, r.lng!]}
            radius={r.orders_last_90d > 0 ? 7 : 5}
            pathOptions={{
              color: r.orders_last_90d > 0 ? '#51BFAA' : '#fff',
              weight: r.orders_last_90d > 0 ? 2 : 1,
              fillColor: scoreColor(r.score),
              fillOpacity: 0.9,
            }}
            eventHandlers={{ click: () => onSelectCompany?.(r.id) }}
          >
            <Tooltip direction="top" offset={[0, -4]}>
              <strong>{r.name}</strong>
              <div>
                {CATEGORY_LABEL[r.category]} · score {r.score ?? '—'}
              </div>
              <div>
                {formatDistance(r.nearest_distance_m)}
                {r.orders_last_90d > 0 ? ` · ${r.orders_last_90d} warm orders` : ''}
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </LayerGroup>
    </MapContainer>
  );
}
